<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

const { t } = useI18n();

const showDropdown = ref(false);
const searchQuery = ref('');

// v6.4.25: Mobile/PWA detection for shorter label text
const isMobileOrPwa = ref(false);
const mobileQuery =
  typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 768px)')
    : null;

const checkMobileOrPwa = () => {
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const isMobile = mobileQuery?.matches ?? false;
  isMobileOrPwa.value = isMobile || isStandalone;
};

onMounted(() => {
  checkMobileOrPwa();
  mobileQuery?.addEventListener?.('change', checkMobileOrPwa);
});

onUnmounted(() => {
  mobileQuery?.removeEventListener?.('change', checkMobileOrPwa);
});

// Guard: only render when a conversation is selected
const currentChat = useMapGetter('getSelectedChat');
const hasConversation = computed(() => Boolean(currentChat.value?.id));

const {
  activeLabels,
  accountLabels,
  savedLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const filteredLabels = computed(() => {
  let labels = accountLabels.value;
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    labels = labels.filter(label =>
      (label.title || '').toLowerCase().includes(query)
    );
  }
  // Sort: assigned labels first, then maintain original order
  return [...labels].sort((a, b) => {
    const aActive = savedLabels.value.includes(a.title);
    const bActive = savedLabels.value.includes(b.title);
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return 0;
  });
});

const hasLabels = computed(() => accountLabels.value.length > 0);
const labelCount = computed(() => activeLabels.value.length);

/**
 * Returns the color of the first active label, falling back to a neutral gray.
 * Handles edge cases where label.color is null/empty in the database.
 */
const firstLabelColor = computed(() => {
  if (activeLabels.value.length > 0) {
    return activeLabels.value[0].color || '#6b7280';
  }
  return '#6b7280';
});

const buttonLabel = computed(() => {
  if (labelCount.value === 0) {
    // v6.4.25: No mobile/PWA, texto curto 'Etiquetas' em vez de 'Etiquetar conversa'
    return isMobileOrPwa.value
      ? t('CONVERSATION.LABELS_HEADER.LABEL_SHORT')
      : t('CONVERSATION.LABELS_HEADER.LABEL_CONVERSATION');
  }
  if (labelCount.value === 1) {
    return activeLabels.value[0].title || '';
  }
  return t('CONVERSATION.LABELS_HEADER.SELECTED_COUNT', {
    count: labelCount.value,
  });
});

const isLabelActive = label => {
  return savedLabels.value.includes(label.title);
};

const toggleLabel = label => {
  if (isLabelActive(label)) {
    removeLabelFromConversation(label.title);
  } else {
    addLabelToConversation(label);
  }
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
  if (!showDropdown.value) {
    searchQuery.value = '';
  }
};

const closeDropdown = () => {
  showDropdown.value = false;
  searchQuery.value = '';
};

const onKeydown = event => {
  if (event.key === 'Escape' && showDropdown.value) {
    closeDropdown();
  }
};

/**
 * Returns a safe CSS color value, falling back to a neutral gray
 * when the label.color is null/undefined/empty.
 */
const safeLabelColor = label => label.color || '#6b7280';
</script>

<template>
  <div
    v-if="hasConversation"
    v-on-click-outside="closeDropdown"
    class="relative flex items-center"
    @keydown="onKeydown"
  >
    <!-- Trigger Button -->
    <!-- v6.4.25: font text-[13px] (era text-xs/12px) -->
    <button
      class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[13px] font-medium transition-all duration-200 border border-solid cursor-pointer select-none whitespace-nowrap max-w-[180px] border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 dark:border-n-weak dark:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
      @click="toggleDropdown"
    >
      <span
        class="w-2.5 h-2.5 rounded-sm flex-shrink-0"
        :style="{ backgroundColor: firstLabelColor }"
      />
      <span class="truncate">{{ buttonLabel }}</span>
      <i
        class="i-lucide-chevron-down w-3 h-3 flex-shrink-0 transition-transform duration-200"
        :class="{ 'rotate-180': showDropdown }"
      />
    </button>

    <!-- Dropdown Panel -->
    <div
      v-show="showDropdown"
      class="absolute top-full mt-1.5 ltr:left-0 rtl:right-0 z-50 w-64 rounded-xl border border-solid shadow-lg border-n-weak bg-n-background dark:border-n-weak dark:bg-n-background"
    >
      <!-- Search -->
      <div class="p-2 border-b border-solid border-n-weak">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('CONVERSATION.LABELS_HEADER.SEARCH_PLACEHOLDER')"
            class="w-full px-2 py-1.5 text-xs rounded-lg border border-solid border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand dark:bg-n-alpha-1 dark:border-n-weak dark:text-n-slate-12"
          />
        </div>
      </div>

      <!-- Label List -->
      <!-- v6.4.25: max-h-52 (208px) → max-h-80 (320px) = +50% altura -->
      <ul v-if="hasLabels" class="m-0 p-1 list-none max-h-80 overflow-y-auto">
        <li v-if="filteredLabels.length === 0" class="px-3 py-2 text-center">
          <span class="text-xs text-n-slate-10">
            {{ t('CONVERSATION.LABELS_HEADER.NO_LABELS') }}
          </span>
        </li>
        <li
          v-for="label in filteredLabels"
          :key="label.id"
          class="rounded-lg cursor-pointer transition-colors duration-150"
        >
          <label
            class="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-alpha-2 transition-colors duration-150"
            :class="{ 'bg-n-alpha-1': isLabelActive(label) }"
          >
            <span
              class="w-3 h-3 rounded flex-shrink-0 border border-solid border-n-weak/50"
              :style="{ backgroundColor: safeLabelColor(label) }"
            />
            <!-- v6.4.25: text-[13px] + font-normal (era text-xs/font-medium, muito contraste) -->
            <span
              class="flex-1 text-[13px] font-normal text-n-slate-11 truncate"
            >
              {{ label.title }}
            </span>
            <input
              type="checkbox"
              :checked="isLabelActive(label)"
              class="form-checkbox w-3.5 h-3.5 rounded text-n-brand border-n-weak cursor-pointer accent-current flex-shrink-0"
              @change="toggleLabel(label)"
            />
          </label>
        </li>
      </ul>

      <!-- No labels state -->
      <div v-else class="px-3 py-4 text-center">
        <span class="text-xs text-n-slate-10">
          {{ t('CONVERSATION.LABELS_HEADER.NO_LABELS') }}
        </span>
      </div>
    </div>
  </div>
</template>
